import { useEffect, useState } from 'react'
import MuiCheckbox from '@mui/material/Checkbox'
import Alert from '@mui/material/Alert'

import {
  Address,
  ApplianceType,
  ApplianceVersion,
  Input,
  InputAdminStatus,
  IpPortMode,
  OccupiedPort,
  RtpInputPort,
} from 'common/api/v1/types'
import { Checkbox, Select, TextInput } from '../../../../common/Form'
import { createDefaultFiledValues, isCoreNode, makeAddressOptions } from '../../../../../utils'
import { isVaApplianceType } from 'common/applianceTypeUtil'
import { supportsFec } from 'common/versions'
import { WhitelistCidrBlockAutoComplete } from '../../../../common/WhitelistCidrBlockAutoComplete'
import { SuggestedLocalPortTextField } from '../../../../common/SuggestedPortTextField'
import { useFormContext } from 'react-hook-form'
import { AllowUncontrolled } from 'src/components/common/Form/RHF'

export enum RtpFields {
  address = 'address',
  port = 'port',
  fec = 'fec',
  multicastAddress = 'multicastAddress',
  multicastSource = 'multicastSource',
  whitelistCidrBlock = 'whitelistCidrBlock',
  failoverPriority = 'failoverPriority',
}

export const rtpDefaults = () =>
  createDefaultFiledValues(Object.keys(RtpFields), [RtpFields.fec], {
    [RtpFields.whitelistCidrBlock]: [],
  })

interface RtpFormProps {
  addresses: Array<Address>
  portIndex: number
  applianceType: ApplianceType
  applianceVersion: ApplianceVersion
  namePrefix: string
  occupiedPorts: OccupiedPort[]
  adminStatus: InputAdminStatus
}

const RtpForm = ({
  addresses,
  namePrefix,
  applianceType,
  applianceVersion,
  portIndex: _portIndex,
  occupiedPorts,
  adminStatus,
}: RtpFormProps) => {
  const { getValues, setValue, watch } = useFormContext<Input & AllowUncontrolled>()

  const isCoreAppliance = isCoreNode(applianceType)

  const rtpInputPort: RtpInputPort = getValues(namePrefix)

  const fecSelector = `${namePrefix}.${RtpFields.fec}`
  const isRtcpEnabled = false // TODO: Make configurable when RTP RTCP is supported by ristserver
  const isFecEnabled: boolean = getValues(fecSelector)
  const showFecCheckbox = supportsFec(applianceType, applianceVersion)
  watch(fecSelector)

  const getMulticast = () => ({
    address: rtpInputPort.multicastAddress,
    source: rtpInputPort.multicastSource,
  })
  const { address, source } = getMulticast()
  const [isMulticast, setIsMulticast] = useState(!!(address || source))
  const [multicast, setMulticast] = useState(getMulticast())

  // Get all RTP ports across all appliance sections
  const getAllRtpPorts = (): { port: RtpInputPort; sectionKey: string; sectionIndex: number }[] => {
    const allPorts: { port: RtpInputPort; sectionKey: string; sectionIndex: number }[] = []
    const formValues = getValues()

    // Check if _applianceSection exists and is an array
    if (formValues._applianceSection && Array.isArray(formValues._applianceSection)) {
      formValues._applianceSection.forEach((section: any, sectionIndex: number) => {
        if (section?.ports && Array.isArray(section.ports)) {
          section.ports.forEach((p: any) => {
            if (p.mode === IpPortMode.rtp) {
              allPorts.push({
                port: p,
                sectionKey: `_applianceSection.${sectionIndex}`,
                sectionIndex,
              })
            }
          })
        }
      })
    }

    return allPorts
  }

  const allRtpPorts = getAllRtpPorts()

  // Extract section index from namePrefix (e.g., "_applianceSection.0.ports.1" -> 0)
  const currentSectionIndex = parseInt(namePrefix.match(/_applianceSection\.(\d+)/)?.[1] ?? '-1')

  // Show checkbox if this is not the first RTP port overall
  const isFirstRtpPort = (): boolean => {
    if (allRtpPorts.length === 0) return true

    // Find current port in all RTP ports
    for (let i = 0; i < allRtpPorts.length; i++) {
      const { port, sectionIndex } = allRtpPorts[i]

      // Check if this is an earlier section
      if (sectionIndex < currentSectionIndex) continue

      // Same section - check port order
      if (sectionIndex === currentSectionIndex) {
        if (port === rtpInputPort) {
          return i === 0 // First port overall
        }
      }
    }

    return false
  }

  const showDash7Checkbox = !isFirstRtpPort()
  const isDash7Redundant = typeof rtpInputPort.failoverPriority !== 'number'

  // Check if RTP ports are on different appliances
  const arePortsOnDifferentAppliances =
    allRtpPorts.length > 1 && new Set(allRtpPorts.map(({ sectionIndex }) => sectionIndex)).size > 1

  // Check if RTP ports have different port numbers
  const havePortNumberMismatch = (): boolean => {
    if (!isDash7Redundant || allRtpPorts.length <= 1) return false

    const rtpPortsWithoutFailover = allRtpPorts.filter(({ port }) => typeof port.failoverPriority !== 'number')

    if (rtpPortsWithoutFailover.length <= 1) return false

    const portNumbers = new Set(rtpPortsWithoutFailover.map(({ port }) => port.port))
    return portNumbers.size > 1
  }

  const addressSelector = `${namePrefix}.${RtpFields.address}`
  const selectedSourceAddress = getValues(addressSelector)
  const portSelector = `${namePrefix}.${RtpFields.port}`

  useEffect(() => {
    if (!isMulticast) {
      setMulticast(getMulticast())
      setValue(`${namePrefix}.${RtpFields.multicastAddress}`, '')
      setValue(`${namePrefix}.${RtpFields.multicastSource}`, '')
    } else {
      setValue(`${namePrefix}.${RtpFields.multicastAddress}`, multicast.address)
      setValue(`${namePrefix}.${RtpFields.multicastSource}`, multicast.source)
    }
  }, [isMulticast, setValue])

  return (
    <>
      {showDash7Checkbox && (
        <Checkbox
          label="Identical RTP streams (SMPTE 2022-7)"
          tooltip={`Requires that all interfaces receive binary equal streams, e.g. from the same encoder. Can merge streams across different appliances.`}
          control={
            <MuiCheckbox
              id={`${namePrefix}.${RtpFields.failoverPriority}`}
              name={`${namePrefix}.${RtpFields.failoverPriority}`}
              checked={isDash7Redundant}
              onChange={(e) => {
                // Update all RTP ports across all appliance sections
                const formValues = getValues()

                if (formValues._applianceSection && Array.isArray(formValues._applianceSection)) {
                  formValues._applianceSection.forEach((section: any, sectionIndex: number) => {
                    if (section?.ports && Array.isArray(section.ports)) {
                      let rtpPortIndex = 0
                      const updatedPorts = section.ports.map((port: any) => {
                        if (port.mode === IpPortMode.rtp) {
                          const newPort = {
                            ...port,
                            [RtpFields.failoverPriority]: e.target.checked ? undefined : rtpPortIndex,
                          }
                          rtpPortIndex++
                          return newPort
                        }
                        return port
                      })
                      setValue(`_applianceSection.${sectionIndex}.ports`, updatedPorts)
                    }
                  })
                }
              }}
            />
          }
        />
      )}
      {showDash7Checkbox && isDash7Redundant && arePortsOnDifferentAppliances && (
        <Alert severity={havePortNumberMismatch() ? 'warning' : 'info'} sx={{ mt: 1, mb: 2 }}>
          {havePortNumberMismatch()
            ? "RTP streams will be merged across appliances into a single channel. Warning: Port numbers don't match - ensure streams are binary identical."
            : 'RTP streams will be merged across appliances into a single channel'}
        </Alert>
      )}
      <Select
        name={addressSelector}
        label="Address"
        required
        options={makeAddressOptions(selectedSourceAddress, addresses)}
        newLine
        validators={{
          addressIn: { addresses },
        }}
      />

      <SuggestedLocalPortTextField
        name={portSelector}
        label={'Port'}
        namePrefix={namePrefix}
        required={true}
        portInfo={{
          mode: IpPortMode.rtp,
          isInput: true,
          isFec: isFecEnabled,
          isRtpRtcp: isRtcpEnabled,
          isMulticast,
          existingLogicalPortsOnSameNic: occupiedPorts,
          isPortDisabled: adminStatus === InputAdminStatus.off,
        }}
      />

      {showFecCheckbox && <Checkbox name={fecSelector} label="Use Forward Error Correction?" />}
      <Checkbox
        control={
          <MuiCheckbox
            checked={isMulticast}
            onChange={(e) => setIsMulticast(e.target.checked)}
            id={`${namePrefix}.multicast`}
            name={`${namePrefix}.multicast`}
          />
        }
        label="Is this port multicast?"
        newLine
      />
      {isMulticast && (
        <>
          <TextInput
            name={`${namePrefix}.${RtpFields.multicastAddress}`}
            label="Multicast address"
            validators={{
              multicastAddress: {},
            }}
            newLine
          />
          <TextInput
            name={`${namePrefix}.${RtpFields.multicastSource}`}
            label="Source specific multicast address"
            validators={{
              ip: {},
            }}
          />
        </>
      )}
      {!isVaApplianceType(applianceType) && (
        <WhitelistCidrBlockAutoComplete
          name={`${namePrefix}.${RtpFields.whitelistCidrBlock}`}
          required={isCoreAppliance}
        />
      )}
    </>
  )
}

export default RtpForm
